import { Button } from '@iwoca/orion';
import RiveComponent, { Alignment, Fit, Layout } from '@rive-app/react-canvas';

import { useGetBuyerSpendingLimitRequest } from '../../../../api/lending/lapiHooks';
// @ts-ignore
import AccountSuccessTickRive from '../../../../assets/AccountSuccessTickRive.riv';
import DirectorCheckIcon from '../../../../assets/DirectorCheckIcon.svg';
import ExternalLinkIcon from '../../../../assets/ExternalLinkIcon.svg';
import IDCheckIcon2 from '../../../../assets/IDCheckIcon2.svg';
import LinkCardPaymentsIcon from '../../../../assets/LinkCardPaymentsIcon.svg';
import { LoadingScreen } from '../../../../components/LoadingScreen/LoadingScreen';
import { useSellerWhitelabelling } from '../../../../hooks/useSellerWhiteLabelling.hook';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { roundTo } from '../../../../utils/roundTo';
import { useFormalOfferWithDuration } from '../../../Checkout/hooks/useFormalOfferWithDuration';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import {
  redirectToMyAccount,
  redirectToSecurityChecks,
} from '../../useRedirectToAccountPages';

export const SpendingLimitRequestApproved = () => {
  const { formalOffer, loadingFormalOffer } = useFormalOfferWithDuration(3);
  const { spendingLimitRequest, loadingSpendingLimitRequest } =
    useGetBuyerSpendingLimitRequest();

  // White-label theme for spending limits
  useSellerWhitelabelling(spendingLimitRequest?.data?.seller_handle);

  if (loadingFormalOffer || loadingSpendingLimitRequest) {
    return <LoadingScreen />;
  }
  if (!formalOffer) {
    redirectToMyAccount();
  }

  const latestFormalOfferAvailableCredit = formalOffer?.available_credit || 0;
  const latestFormalOfferInterestRate = formalOffer?.interest_rates?.[0].rate
    ? roundTo(formalOffer?.interest_rates?.[0].rate * 100, 2).toString()
    : null;

  const sellerProductPricingType3m =
    spendingLimitRequest?.data?.seller_product_pricing_type_3m;

  const interestRateText =
    sellerProductPricingType3m &&
    sellerProductPricingType3m.startsWith('SELLER_PAYS')
      ? 'interest-free'
      : 'at an interest rate of ' +
        latestFormalOfferInterestRate +
        '% per month';

  return (
    <div className="min-h-screen bg-secondary-95">
      <div className="p-3xl max-600:p-r">
        <div className="mx-auto max-w-[400px] rounded-l border border-secondary-90 bg-white p-xl text-center max-600:p-r">
          <RiveComponent
            src={AccountSuccessTickRive}
            className="mx-auto h-[200px] w-[200px] max-600:h-[120px] max-600:w-[120px]"
            layout={
              new Layout({
                fit: Fit.Contain,
                alignment: Alignment.TopCenter,
              })
            }
          />
          <div className="mb-l text-2xl font-bold text-success-drk max-600:text-xl">
            Great news!
          </div>
          <div className="mb-s text-m font-med max-600:text-s">
            You can spend up to
          </div>
          <div className="mb-xs text-6xl font-bold max-600:text-5xl">
            {displayCurrency(latestFormalOfferAvailableCredit)}
          </div>
          <div className="mb-xs text-m font-med max-600:text-s">
            {interestRateText}
          </div>

          <div className="inline-block">
            <a
              href={
                'https://support.iwoca.co.uk/en/articles/3696498-paying-with-pay-later'
              }
              className="mb-xl flex items-center gap-s text-xs font-reg text-primary-40"
            >
              How interest works with iwocaPay
              <div style={{ display: 'flex' }}>
                <img src={ExternalLinkIcon} alt="" />
              </div>
            </a>
          </div>
          <div className="mb-l text-xs font-reg">
            We'll reassess your spending limit every 30 days &mdash; so your
            available amount might change after that.
          </div>
          <hr className="my-m border-0 border-t border-structure-80" />
          <div className="my-l text-left text-l font-bold max-600:text-m">
            A few quick checks and you can start using your spending limit:
          </div>
          <ul className="mb-xl flex list-none flex-col justify-center gap-l p-0 text-left">
            <li className="flex items-center gap-s text-m font-med max-600:text-s">
              <img src={IDCheckIcon2} alt="ID Check Icon" />
              <div>ID check</div>
            </li>
            <li className="flex items-center gap-s text-m font-med max-600:text-s">
              <img src={DirectorCheckIcon} alt="Director Check Icon" />
              <div>Director and Shareholder Information</div>
            </li>
            <li className="flex items-center gap-s text-m font-med max-600:text-s">
              <img src={LinkCardPaymentsIcon} alt="Link Card Payments Icon" />
              <div>Link your debit card for monthly payments</div>
            </li>
          </ul>
          <div className="flex flex-col gap-l">
            <WhiteLabelOverride
              componentType="button"
              disabled={false}
              variant="primary"
            >
              <Button
                className="w-full"
                onClick={() => redirectToSecurityChecks(formalOffer?.offer_id!)}
              >
                Take me to the next step
              </Button>
            </WhiteLabelOverride>
            <WhiteLabelOverride
              componentType="button"
              disabled={false}
              variant="secondary"
            >
              <Button
                variant="secondary"
                className="w-full"
                onClick={redirectToMyAccount}
              >
                I'll do this later
              </Button>
            </WhiteLabelOverride>
          </div>
        </div>
      </div>
    </div>
  );
};
